import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, User, Mail, Phone, ClipboardCheck, CalendarCheck, MessageCircle, Activity } from "lucide-react";
import { api } from "@/lib/api";
import { AdminPage } from "@/pages/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { formatUSD, LEAD_STATUS_META } from "@/lib/format";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import AdminTimeline from "@/components/AdminTimeline";
import AdminBookAppointmentButton from "@/components/AdminBookAppointmentButton";

const AdminCustomerDetail = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    api.get(`/admin/customers/${encodeURIComponent(id)}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [id, tick]);

  return (
    <AdminPage
      title={data?.profile?.full_name || "Loading…"}
      subtitle={data?.profile?.email}
      testId="admin-customer-detail-page"
      action={
        <div className="flex items-center gap-2">
          {data?.profile && (
            <AdminBookAppointmentButton
              customerId={data.profile.id}
              customerName={data.profile.full_name}
              onBooked={() => setTick((n) => n + 1)}
            />
          )}
          <Button asChild variant="outline"><Link to="/admin/customers"><ArrowLeft size={13} /> Back</Link></Button>
        </div>
      }
    >
      {!data ? (
        <div className="h-96 bg-slate-100 rounded-2xl animate-pulse" />
      ) : (
        <>
          <div className="bg-white border border-slate-200 rounded-2xl p-5 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-customer-profile">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1"><User size={11} /> Name</p>
              <p className="mt-1 text-sm font-medium">{data.profile.full_name}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1"><Mail size={11} /> Email</p>
              <p className="mt-1 text-sm">{data.profile.email}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1"><Phone size={11} /> Phone</p>
              <p className="mt-1 text-sm">{data.profile.phone || "—"}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">First seen</p>
              <p className="mt-1 text-sm">{data.profile.created_at?.slice(0, 10) || "—"}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4" data-testid="admin-customer-totals">
            <div className="bg-brand text-white rounded-2xl p-4"><p className="text-[10px] uppercase tracking-widest">Leads</p><p className="mt-1 font-display text-2xl">{data.totals.total_leads}</p></div>
            <div className="bg-white border border-slate-200 rounded-2xl p-4"><p className="text-[10px] uppercase tracking-widest text-slate-500">Appts</p><p className="mt-1 font-display text-2xl text-slate-900">{data.totals.total_appts}</p></div>
            <div className="bg-emerald-600 text-white rounded-2xl p-4"><p className="text-[10px] uppercase tracking-widest">Showed up</p><p className="mt-1 font-display text-2xl">{data.totals.total_showed_up}</p></div>
            <div className="bg-gold text-white rounded-2xl p-4"><p className="text-[10px] uppercase tracking-widest">Sold</p><p className="mt-1 font-display text-2xl">{data.totals.total_sold}</p></div>
          </div>

          <div className="mt-6 grid lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2">
              <Tabs defaultValue="timeline">
                <TabsList data-testid="admin-customer-tabs">
                  <TabsTrigger value="timeline" data-testid="admin-customer-tab-timeline"><Activity size={13} /> Timeline</TabsTrigger>
                  <TabsTrigger value="leads" data-testid="admin-customer-tab-leads"><ClipboardCheck size={13} /> Leads</TabsTrigger>
                  <TabsTrigger value="appts" data-testid="admin-customer-tab-appts"><CalendarCheck size={13} /> Appointments</TabsTrigger>
                  <TabsTrigger value="chats" data-testid="admin-customer-tab-chats"><MessageCircle size={13} /> Chats</TabsTrigger>
                </TabsList>

                <TabsContent value="timeline" className="mt-4">
                  <AdminTimeline endpoint={`/admin/customers/${detail.id}/timeline`} />
                </TabsContent>

                <TabsContent value="leads" className="mt-4 space-y-3">
                  {data.leads.length === 0 ? (
                    <p className="text-sm text-slate-500 py-8 text-center">No leads yet.</p>
                  ) : data.leads.map((l) => {
                    const meta = LEAD_STATUS_META[l.status] || LEAD_STATUS_META.new;
                    return (
                      <div key={l.id} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-4" data-testid={`admin-customer-lead-${l.id}`}>
                        {l.vehicle?.images?.[0] && <img src={l.vehicle.images[0]} className="w-16 h-12 object-cover rounded" alt="" />}
                        <div className="flex-1 min-w-0">
                          <Link to={`/admin/leads/${l.id}`} className="text-sm font-semibold text-brand hover:underline">
                            {l.vehicle ? `${l.vehicle.year} ${l.vehicle.make} ${l.vehicle.model}` : "Vehicle unavailable"}
                          </Link>
                          <p className="text-xs text-slate-500 mt-0.5">{l.dealer?.name} · {new Date(l.created_at).toLocaleDateString()}</p>
                        </div>
                        <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold ${meta.tone}`}>{meta.label}</span>
                      </div>
                    );
                  })}
                </TabsContent>

                <TabsContent value="appts" className="mt-4 space-y-3">
                  {data.appointments.length === 0 ? (
                    <p className="text-sm text-slate-500 py-8 text-center">No appointments yet.</p>
                  ) : data.appointments.map((a) => (
                    <div key={a.id} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3" data-testid={`admin-customer-appt-${a.id}`}>
                      <div className="flex-1">
                        <p className="text-sm font-medium">{new Date(a.scheduled_at).toLocaleString()}</p>
                        <p className="text-xs text-slate-500">Status: {a.status} {a.status === "showed_up" && <span className="text-gold font-semibold">· +$300</span>}</p>
                      </div>
                    </div>
                  ))}
                </TabsContent>

                <TabsContent value="chats" className="mt-4 space-y-3">
                  {data.threads.length === 0 ? (
                    <p className="text-sm text-slate-500 py-8 text-center">No chat threads yet.</p>
                  ) : data.threads.map((t) => (
                    <div key={t.id} className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`admin-customer-thread-${t.id}`}>
                      <p className="text-sm font-medium">Thread {t.id.slice(0, 8)} {t.is_closed && <span className="text-[10px] uppercase text-slate-400 ml-2">Closed</span>}</p>
                      <p className="text-xs text-slate-500 mt-1 line-clamp-2">"{t.last_message_preview || 'No messages yet'}"</p>
                    </div>
                  ))}
                </TabsContent>
              </Tabs>
            </div>

            {/* Timeline */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="admin-customer-timeline">
              <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1"><Activity size={12} /> Activity timeline</p>
              <ul className="mt-3 space-y-3 max-h-[500px] overflow-auto">
                {data.timeline.length === 0 ? (
                  <li className="text-sm text-slate-400">No activity yet.</li>
                ) : data.timeline.map((t, i) => (
                  <li key={i} className="flex gap-3 text-sm border-l-2 border-brand/30 pl-3">
                    <div className="flex-1">
                      <p className="font-medium text-slate-800">{t.label}</p>
                      <p className="text-[11px] text-slate-500 mt-0.5">{new Date(t.at).toLocaleString()}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </>
      )}
    </AdminPage>
  );
};

export default AdminCustomerDetail;
